/**
 * MapLibre map creation and lifecycle.
 *
 * Registers the PMTiles protocol (for the offline vector basemap) and wires a
 * runtime safety net: if the vector source fails to load — e.g. the user hasn't
 * generated `region.pmtiles` yet, or it 404s — the map swaps to the online
 * raster fallback so there is always a visible basemap.
 */

import maplibregl, { Map as MLMap } from 'maplibre-gl';
import { Protocol } from 'pmtiles';
import {
  REGION_BBOX,
  hasOfflineBasemap,
  initialStyle,
  rasterStyle,
} from './basemap';

let protocolRegistered = false;

function ensurePmtilesProtocol(): void {
  if (protocolRegistered) return;
  const protocol = new Protocol();
  maplibregl.addProtocol('pmtiles', protocol.tile);
  protocolRegistered = true;
}

/** Create the MapLibre map bound to the given container element. */
export function createMap(container: HTMLElement): MLMap {
  ensurePmtilesProtocol();

  const centerLng = (REGION_BBOX.west + REGION_BBOX.east) / 2;
  const centerLat = (REGION_BBOX.south + REGION_BBOX.north) / 2;

  const map = new maplibregl.Map({
    container,
    style: initialStyle(),
    center: [centerLng, centerLat],
    zoom: 7.4,
    // Hebrew accessible names for MapLibre's built-in controls + popup close.
    locale: {
      'NavigationControl.ZoomIn': 'התקרבות',
      'NavigationControl.ZoomOut': 'התרחקות',
      'NavigationControl.ResetBearing': 'איפוס כיוון',
      'GeolocateControl.FindMyLocation': 'המיקום שלי',
      'GeolocateControl.LocationNotAvailable': 'מיקום לא זמין',
      'Popup.Close': 'סגור',
      'AttributionControl.ToggleAttribution': 'קרדיטים',
      'AttributionControl.MapFeedback': 'משוב על המפה',
    },
    // Keep the user within the cached region (padded a little for comfort).
    maxBounds: [
      [REGION_BBOX.west - 0.4, REGION_BBOX.south - 0.35],
      [REGION_BBOX.east + 0.4, REGION_BBOX.north + 0.35],
    ],
    attributionControl: { compact: true },
    // Our markers carry the semantics; keep the base clean and fast.
    fadeDuration: 120,
  });

  map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-left');
  map.addControl(new maplibregl.GeolocateControl({ trackUserLocation: true }), 'top-left');

  let fellBack = false;
  if (hasOfflineBasemap) {
    // If the vector basemap errors at runtime, fall back to raster once.
    map.on('error', (e) => {
      const msg = String((e && e.error && e.error.message) || '');
      // MapLibre doesn't attach a sourceId on the vector-source load-error path,
      // so match on the message. Cover the real failure texts: a 404 on the
      // pmtiles file ("Bad response code: 404"), an offline fetch ("Failed to
      // fetch"), and malformed/absent archives ("magic number", "PMTiles").
      if (
        !fellBack &&
        /pmtiles|protomaps|region\.pmtiles|bad response code|magic number|failed to fetch/i.test(msg)
      ) {
        fellBack = true;
        console.warn('[basemap] vector basemap failed, falling back to raster:', msg);
        map.setStyle(rasterStyle());
      }
    });
  }

  return map;
}

export { maplibregl };
